import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, Contrast, Type, Minus, Plus, MousePointer, Image, Keyboard, RotateCcw, Accessibility } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('a11y_dismissed') === '1');
  const [settings, setSettings] = useState({
    colorMode: 'normal',
    fontSize: 100,
    lineSpacing: 0,
    letterSpacing: 0,
    highlightLinks: false,
    readableFont: false,
    hideImages: false,
    bigCursorDark: false,
    bigCursorLight: false,
    keyboardNav: false
  });

  useEffect(() => {
    applyStyles();
  }, [settings]);

  const applyStyles = () => {
    const root = document.documentElement;
    const body = document.body;

    body.classList.remove('acc-dark', 'acc-light', 'acc-sepia', 'acc-high-contrast', 'acc-invert', 
      'acc-highlight-links', 'acc-readable-font', 'acc-hide-images', 'acc-big-cursor-dark', 'acc-big-cursor-light', 'acc-keyboard-nav');

    if (settings.colorMode === 'dark') body.classList.add('acc-dark');
    if (settings.colorMode === 'light') body.classList.add('acc-light');
    if (settings.colorMode === 'sepia') body.classList.add('acc-sepia');
    if (settings.colorMode === 'highContrast') body.classList.add('acc-high-contrast');
    if (settings.colorMode === 'invertColors') body.classList.add('acc-invert');

    root.style.fontSize = `${settings.fontSize}%`;
    body.style.lineHeight = settings.lineSpacing === 0 ? '' : `${1.5 + (settings.lineSpacing * 0.25)}`;
    body.style.letterSpacing = settings.letterSpacing === 0 ? '' : `${settings.letterSpacing}px`;

    if (settings.highlightLinks) body.classList.add('acc-highlight-links');
    if (settings.readableFont) body.classList.add('acc-readable-font');
    if (settings.hideImages) body.classList.add('acc-hide-images');
    if (settings.bigCursorDark) body.classList.add('acc-big-cursor-dark');
    if (settings.bigCursorLight) body.classList.add('acc-big-cursor-light');
    if (settings.keyboardNav) body.classList.add('acc-keyboard-nav');
  };

  const resetAll = () => {
    setSettings({
      colorMode: 'normal',
      fontSize: 100,
      lineSpacing: 0,
      letterSpacing: 0,
      highlightLinks: false,
      readableFont: false,
      hideImages: false,
      bigCursorDark: false,
      bigCursorLight: false,
      keyboardNav: false
    });
    document.documentElement.style.fontSize = '100%';
    document.body.style.lineHeight = '';
    document.body.style.letterSpacing = '';
  };

  const ColorModeButton = ({ mode, icon, label, active }) => (
    <button
      onClick={() => setSettings(prev => ({ ...prev, colorMode: mode }))}
      className={`flex flex-col items-center justify-center p-2 sm:p-3 rounded-xl border-2 transition-all ${
        active ? 'border-cyan-500 bg-cyan-50' : 'border-stone-200 hover:border-cyan-300'
      }`}
    >
      {icon}
      <span className="text-[10px] sm:text-xs mt-1 text-stone-600">{label}</span>
    </button>
  );

  const ToggleButton = ({ active, onClick, icon, label }) => (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-2 sm:p-3 rounded-xl border-2 transition-all ${
        active ? 'border-cyan-500 bg-cyan-50' : 'border-stone-200 hover:border-cyan-300'
      }`}
    >
      {icon}
      <span className="text-[10px] sm:text-xs mt-1 text-stone-600 leading-tight">{label}</span>
    </button>
  );

  return (
    <>
      {/* Floating Button - Side tab style */}
      {!dismissed && (
        <div className="fixed right-0 top-1/3 z-50" data-testid="accessibility-wrapper">
          <button
            onClick={() => { setDismissed(true); sessionStorage.setItem('a11y_dismissed', '1'); }}
            className="absolute -top-2 -left-2 w-5 h-5 bg-stone-400 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-xs leading-none z-10 transition-colors shadow"
            data-testid="accessibility-dismiss-btn"
          >
            ×
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg flex items-center justify-center transition-all rounded-l-lg px-1.5 py-3"
            aria-label="כלי נגישות"
            title="נגישות"
            data-testid="accessibility-toggle"
          >
            <Accessibility className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Panel - Full Screen on Mobile */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          <div 
            className="fixed inset-x-0 bottom-0 sm:inset-auto sm:right-0 sm:top-0 sm:h-full z-50 bg-gradient-to-b from-cyan-50 to-purple-50 w-full sm:w-80 max-h-[80vh] sm:max-h-full rounded-t-3xl sm:rounded-none shadow-2xl overflow-hidden flex flex-col" 
            dir="rtl"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-4 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-lg sm:text-xl" style={{ fontFamily: 'Heebo, sans-serif' }}>כלי נגישות</h3>
              <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-1">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 sm:space-y-6">
              {/* Color Modes */}
              <div className="grid grid-cols-3 gap-2">
                <ColorModeButton
                  mode="highContrast"
                  active={settings.colorMode === 'highContrast'}
                  icon={<Contrast className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="ניגודיות"
                />
                <ColorModeButton
                  mode="dark"
                  active={settings.colorMode === 'dark'}
                  icon={<Moon className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="כהה"
                />
                <ColorModeButton
                  mode="light"
                  active={settings.colorMode === 'light'}
                  icon={<Sun className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="בהיר"
                />
                <ColorModeButton
                  mode="sepia"
                  active={settings.colorMode === 'sepia'}
                  icon={<div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-amber-200 border-2 border-amber-400" />}
                  label="חום"
                />
                <ColorModeButton
                  mode="invertColors"
                  active={settings.colorMode === 'invertColors'}
                  icon={<div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-r from-black to-white" />}
                  label="היפוך"
                />
                <ColorModeButton
                  mode="normal"
                  active={settings.colorMode === 'normal'}
                  icon={<RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="רגיל"
                />
              </div>

              {/* Font Size */}
              <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm">
                <p className="text-xs sm:text-sm font-bold text-cyan-700 mb-2 sm:mb-3 text-center">גודל גופן</p>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, fontSize: Math.max(80, prev.fontSize - 10) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <div className="flex-1 text-center">
                    <div className="bg-cyan-500 text-white rounded-full px-3 sm:px-4 py-1.5 sm:py-2 font-bold text-sm sm:text-base">
                      {settings.fontSize}%
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, fontSize: Math.min(150, prev.fontSize + 10) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Line Spacing */}
              <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm">
                <p className="text-xs sm:text-sm font-bold text-cyan-700 mb-2 sm:mb-3 text-center">ריווח שורות</p>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, lineSpacing: Math.max(0, prev.lineSpacing - 1) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <div className="flex-1 px-2">
                    <div className="bg-cyan-200 h-2 rounded-full relative">
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 bg-white border-2 border-cyan-500 rounded-full shadow"
                        style={{ left: `calc(${(settings.lineSpacing / 4) * 100}% - 10px)` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, lineSpacing: Math.min(4, prev.lineSpacing + 1) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Letter Spacing */}
              <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm">
                <p className="text-xs sm:text-sm font-bold text-cyan-700 mb-2 sm:mb-3 text-center">ריווח אותיות</p>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, letterSpacing: Math.max(0, prev.letterSpacing - 1) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <div className="flex-1 px-2">
                    <div className="bg-cyan-200 h-2 rounded-full relative">
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 bg-white border-2 border-cyan-500 rounded-full shadow"
                        style={{ left: `calc(${(settings.letterSpacing / 5) * 100}% - 10px)` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, letterSpacing: Math.min(5, prev.letterSpacing + 1) }))}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-stone-200 flex items-center justify-center hover:border-cyan-500 active:bg-cyan-50"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* More Options */}
              <div className="grid grid-cols-3 gap-2">
                <ToggleButton
                  active={settings.readableFont}
                  onClick={() => setSettings(prev => ({ ...prev, readableFont: !prev.readableFont }))}
                  icon={<span className="text-base sm:text-lg font-bold">א-א</span>}
                  label="גופן קריא"
                />
                <ToggleButton
                  active={settings.hideImages}
                  onClick={() => setSettings(prev => ({ ...prev, hideImages: !prev.hideImages }))}
                  icon={<Image className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="הסתר תמונות"
                />
                <ToggleButton
                  active={settings.highlightLinks}
                  onClick={() => setSettings(prev => ({ ...prev, highlightLinks: !prev.highlightLinks }))}
                  icon={<span className="text-base sm:text-lg underline text-cyan-600">🔗</span>}
                  label="הדגש קישורים"
                />
                <ToggleButton
                  active={settings.keyboardNav}
                  onClick={() => setSettings(prev => ({ ...prev, keyboardNav: !prev.keyboardNav }))}
                  icon={<Keyboard className="w-5 h-5 sm:w-6 sm:h-6 text-stone-700" />}
                  label="מקלדת"
                />
                <ToggleButton
                  active={settings.bigCursorLight}
                  onClick={() => setSettings(prev => ({ ...prev, bigCursorLight: !prev.bigCursorLight, bigCursorDark: false }))}
                  icon={<MousePointer className="w-5 h-5 sm:w-6 sm:h-6 text-stone-400" />}
                  label="סמן בהיר"
                />
                <ToggleButton
                  active={settings.bigCursorDark}
                  onClick={() => setSettings(prev => ({ ...prev, bigCursorDark: !prev.bigCursorDark, bigCursorLight: false }))}
                  icon={<MousePointer className="w-5 h-5 sm:w-6 sm:h-6 text-stone-900 fill-stone-900" />}
                  label="סמן כהה"
                />
              </div>

              {/* Reset Button */}
              <button
                onClick={resetAll}
                className="w-full py-2.5 sm:py-3 bg-red-100 text-red-600 rounded-xl font-bold hover:bg-red-200 active:bg-red-300 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                איפוס הגדרות
              </button>

              {/* Accessibility Statement Link */}
              <a
                href="/accessibility"
                className="block text-center text-xs sm:text-sm text-cyan-600 hover:underline py-2"
              >
                הצהרת נגישות
              </a>
            </div>
          </div>
        </>
      )}

      {/* Global Accessibility Styles */}
      <style>{`
        .acc-dark {
          filter: invert(1) hue-rotate(180deg);
        }
        .acc-dark img, .acc-dark video {
          filter: invert(1) hue-rotate(180deg);
        }
        .acc-light {
          background-color: #fff !important;
          color: #000 !important;
        }
        .acc-sepia {
          filter: sepia(40%);
        }
        .acc-high-contrast {
          filter: contrast(1.5);
        }
        .acc-invert {
          filter: invert(1);
        }
        .acc-invert img, .acc-invert video {
          filter: invert(1);
        }
        .acc-highlight-links a {
          background-color: yellow !important;
          color: black !important;
          padding: 2px 4px;
          text-decoration: underline !important;
          outline: 2px solid orange;
        }
        .acc-readable-font, .acc-readable-font * {
          font-family: Arial, Helvetica, sans-serif !important;
        }
        .acc-hide-images img {
          visibility: hidden;
        }
        .acc-big-cursor-dark, .acc-big-cursor-dark * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M5.5 3.21V20.8l5.93-5.93 3.49 6.44 2.53-1.37-3.49-6.44h7.54L5.5 3.21z'/%3E%3C/svg%3E"), auto !important;
        }
        .acc-big-cursor-light, .acc-big-cursor-light * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='white' stroke='black' stroke-width='1'%3E%3Cpath d='M5.5 3.21V20.8l5.93-5.93 3.49 6.44 2.53-1.37-3.49-6.44h7.54L5.5 3.21z'/%3E%3C/svg%3E"), auto !important;
        }
        .acc-keyboard-nav *:focus {
          outline: 3px solid #0ea5e9 !important;
          outline-offset: 2px !important;
        }
      `}</style>
    </>
  );
};

export default AccessibilityWidget;
